import React from "react";
import { motion } from "framer-motion";
import {
  Waves,
  BrainCog,
  Moon,
  HeartPulse,
  ShieldCheck,
  Zap,
  Activity,
  Sparkles,
  CheckCircle2,
  MessageCircle,
} from "lucide-react";
import { whatsappLink } from "../lib/clinic";

const benefits = [
  {
    icon: BrainCog,
    title: "Calms an over-active nervous system",
    body: "Re-balances the autonomic nervous system by amplifying parasympathetic 'rest-and-digest' tone and easing sympathetic 'fight-or-flight' overdrive — the same overdrive that fuels night-time clenching.",
  },
  {
    icon: Activity,
    title: "Releases jaw, face & neck tension",
    body: "Lower sympathetic load means lower resting muscle tone in the masseter, temporalis and cervical chain — so the jaw isn't gripping itself shut while you sleep.",
  },
  {
    icon: Moon,
    title: "Deeper, more restorative sleep",
    body: "NESA modulates delta-wave activity and supports natural melatonin production, helping the body stay in the slow-wave stages where bruxism episodes are least likely to fire.",
  },
  {
    icon: HeartPulse,
    title: "Lower cortisol, lower stress",
    body: "Improved vagal tone reduces circulating cortisol — easing the daytime stress that primes the jaw to clench at night.",
  },
  {
    icon: ShieldCheck,
    title: "Non-invasive & sub-sensory",
    body: "No needles, no medication, no downtime. The micro-currents are imperceptible — most patients simply relax during the session.",
  },
  {
    icon: Sparkles,
    title: "Works alongside your bruxism plan",
    body: "NESA complements our in-clinic bruxism treatment beautifully: the clinical treatment calms the muscle, NESA calms the nervous system driving it.",
  },
];

const howSteps = [
  {
    step: "01",
    title: "Soft electrodes applied",
    body: "Four limb cables sit gently on the wrists and ankles, plus one directional electrode programmed for your case. Nothing penetrates the skin.",
  },
  {
    step: "02",
    title: "Sub-sensory micro-currents (1–14 Hz)",
    body: "Imperceptible low-frequency currents travel through 24 peripheral input pathways into the central nervous system — mimicking the body's own bioelectric signalling.",
  },
  {
    step: "03",
    title: "Vagal pathways re-tuned",
    body: "Signals reach the vagus network, lifting parasympathetic activity, settling the sympathetic system, and re-polarising over-excited neurons.",
  },
  {
    step: "04",
    title: "Compounding calm",
    body: "Effects build session-on-session (hormesis). Sleep deepens, cortisol falls, and the muscles around the jaw stop bracing.",
  },
];

const protocol = [
  { k: "Typical course", v: "10 sessions" },
  { k: "Frequency", v: "2× per week" },
  { k: "Course length", v: "4–5 weeks" },
  { k: "Sensation", v: "Imperceptible" },
  { k: "Downtime", v: "None" },
  { k: "Side effects", v: "None reported" },
];

export const Nesa = () => {
  return (
    <section
      id="nesa"
      data-testid="nesa-section"
      className="relative bg-rb-bg-2 border-y border-rb-border"
    >
      {/* Soft background accents */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#93C5FD]/12 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-[460px] h-[460px] rounded-full bg-[#93C5FD]/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        {/* Header */}
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="font-overline mb-5 inline-flex items-center gap-2"
          >
            <Waves size={14} strokeWidth={1.75} className="text-[#93C5FD]" />
            NESA® non-invasive neuromodulation
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-rb-text text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight"
          >
            Turn down the body's alarm.
            <br />
            <span className="italic text-[#93C5FD]">
              Switch on its repair mode.
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 text-base lg:text-lg text-rb-text-2 leading-relaxed"
          >
            Most chronic jaw clenching and teeth grinding is fuelled by a nervous
            system stuck in overdrive. <strong className="text-rb-text">NESA®
            neuromodulation</strong> is a non-invasive, drug-free technology that
            gently rebalances the autonomic nervous system — quieting the
            excitatory signals that drive muscle bracing and amplifying the calming
            signals that allow the body to rest, repair and let go.
          </motion.p>
        </div>

        {/* The science callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-12 grid lg:grid-cols-12 gap-8 items-stretch"
        >
          <div className="lg:col-span-7 card-soft p-8 lg:p-10">
            <div className="font-overline mb-3">The science, simplified</div>
            <h3 className="font-serif-display text-2xl lg:text-3xl text-rb-text leading-snug">
              Two nervous systems. One needs to lead — the other needs to rest.
            </h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.03]">
                <div className="flex items-center gap-2 text-[#FCA5A5]">
                  <Zap size={16} strokeWidth={1.75} />
                  <span className="font-overline" style={{ color: "#FCA5A5" }}>
                    Sympathetic (excitatory)
                  </span>
                </div>
                <p className="mt-3 text-sm text-rb-text-2 leading-relaxed">
                  The "fight-or-flight" branch. When chronically dominant: raised
                  cortisol, shallow sleep, bracing muscles, and a jaw that grips
                  itself shut at night.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.03]">
                <div className="flex items-center gap-2 text-[#93C5FD]">
                  <HeartPulse size={16} strokeWidth={1.75} />
                  <span className="font-overline" style={{ color: "#93C5FD" }}>
                    Parasympathetic (calming)
                  </span>
                </div>
                <p className="mt-3 text-sm text-rb-text-2 leading-relaxed">
                  The "rest-and-digest" branch. NESA gently lifts this side via
                  the vagus network — so muscles release, sleep deepens, and the
                  nervous system can recover.
                </p>
              </div>
            </div>
            <p className="mt-6 text-sm text-rb-text-2 leading-relaxed">
              NESA® delivers imperceptible low-frequency micro-currents
              (1–14&nbsp;Hz) through electrodes on the wrists and ankles. Signals
              travel via 24 peripheral input pathways into the central nervous
              system, repolarising over-excited neurons and re-tuning autonomic
              balance.
            </p>
          </div>

          {/* Protocol panel */}
          <div className="lg:col-span-5 rounded-3xl bg-gradient-to-br from-[#0F1B2C] to-[#1E3A8A] text-white p-8 lg:p-10 shadow-[0_30px_60px_-30px_rgba(15,27,44,0.5)]">
            <div className="font-overline text-[#93C5FD]">The protocol</div>
            <h3 className="font-serif-display text-2xl lg:text-3xl mt-2 leading-snug">
              A short, repeatable course — that compounds.
            </h3>
            <p className="mt-4 text-sm text-white/75 leading-relaxed">
              Effects accumulate session-on-session. Most patients begin to
              notice calmer sleep and softer jaw tone inside the first few
              visits.
            </p>
            <dl className="mt-7 divide-y divide-white/10">
              {protocol.map((p) => (
                <div
                  key={p.k}
                  className="flex items-center justify-between py-3"
                >
                  <dt className="text-sm text-white/70">{p.k}</dt>
                  <dd className="text-sm font-medium text-white">{p.v}</dd>
                </div>
              ))}
            </dl>
            <a
              href={whatsappLink(
                "Hi! I'd like to know more about NESA neuromodulation for my jaw clenching / teeth grinding."
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="nesa-cta-whatsapp"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#93C5FD] text-[#0F1B2C] text-sm font-medium hover:bg-white transition-colors"
            >
              <MessageCircle size={16} strokeWidth={1.75} />
              Ask about NESA on WhatsApp
            </a>
          </div>
        </motion.div>

        {/* Benefits grid */}
        <div className="mt-20">
          <div className="flex items-end justify-between gap-6 mb-10">
            <h3 className="font-serif-display text-3xl lg:text-4xl text-rb-text tracking-tight">
              What it can do for a clenching jaw.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`nesa-benefit-${i}`}
                className="card-soft p-6 hover:shadow-[0_14px_30px_-18px_rgba(15,27,44,0.25)] transition-shadow"
              >
                <div className="w-11 h-11 rounded-xl bg-[#93C5FD]/15 text-[#93C5FD] flex items-center justify-center mb-4">
                  <b.icon size={20} strokeWidth={1.5} />
                </div>
                <div className="font-serif-display text-xl text-rb-text leading-snug">
                  {b.title}
                </div>
                <p className="mt-2 text-sm text-rb-text-2 leading-relaxed">
                  {b.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* How it works timeline */}
        <div className="mt-20">
          <h3 className="font-serif-display text-3xl lg:text-4xl text-rb-text tracking-tight mb-10">
            Inside a NESA session.
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {howSteps.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`nesa-step-${i}`}
                className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6"
              >
                <div className="font-serif-display text-[#93C5FD] text-3xl italic">
                  {s.step}
                </div>
                <div className="mt-3 font-medium text-rb-text">{s.title}</div>
                <p className="mt-2 text-sm text-rb-text-2 leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Combo callout */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-20 rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center"
        >
          <div className="lg:col-span-8">
            <div className="font-overline mb-3">Better together</div>
            <h3 className="font-serif-display text-3xl lg:text-4xl text-rb-text leading-tight tracking-tight">
              NESA alongside our in-clinic bruxism programme.
            </h3>
            <p className="mt-4 text-base text-rb-text-2 leading-relaxed max-w-2xl">
              Our in-clinic bruxism treatment calms the over-active jaw muscle
              directly. NESA calms the nervous system driving it. Together they
              tackle bruxism from both ends — the muscle and the message — which
              is why so many patients pair them.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-rb-text-2">
              {[
                "Faster relief than either alone",
                "Better night-time muscle recovery",
                "Improved sleep depth & quality",
                "Lower stress reactivity day-to-day",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <CheckCircle2
                    size={16}
                    strokeWidth={1.75}
                    className="text-[#93C5FD] mt-0.5 shrink-0"
                  />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <a
              href={whatsappLink(
                "Hi! I'd like to ask whether NESA neuromodulation could help with my bruxism alongside in-clinic treatment."
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="nesa-cta-combo-whatsapp"
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} strokeWidth={1.75} />
              Ask if NESA is right for me
            </a>
            <a
              href="#quiz"
              data-testid="nesa-cta-quiz"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              <Sparkles size={15} strokeWidth={1.5} />
              Take the 30-sec quiz
            </a>
            <p className="text-xs text-rb-text-2 leading-relaxed mt-1">
              Educational information only. Suitability assessed at your free
              consultation.
            </p>
          </div>
        </motion.div>

        {/* Source attribution */}
        <p className="mt-10 text-xs text-rb-text-2/80">
          NESA® is a registered technology of NESA World. Information adapted
          from{" "}
          <a
            href="https://nesa.world/en/"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-rb-text"
          >
            nesa.world
          </a>
          .
        </p>
      </div>
    </section>
  );
};
